/**
 * ShareSurveysBanner — "Ready to launch" nudge for un-launched surveys (SYR-68).
 *
 * Shown on the Briefing once a Brand Equity score CAN be computed (≥ the
 * response minimum), but the brand still has active surveys that have collected
 * no responses yet (accessCount falsy). A collapsible, marketer-styled amber
 * banner that surfaces those surveys' share links so the marketer can launch
 * them. Mirrors the dashboard's "Ready to Launch! Share Your Surveys" section.
 */

import React, { useMemo, useState } from 'react';
import { message, Row, Col, Card, Input, Button } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  ShareAltOutlined,
  RightOutlined,
  DownOutlined,
  LinkOutlined,
  CopyOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../../hooks/useAuth';
import { useGetSurveysQuery } from '../../../services/nestjs-base-api';
import { Utils } from '../../../utils/utils';
import { renderFlagIcon, getLanguageName } from '../../../constants/countries';
import { useMarketerScope } from '../scope/MarketerScopeContext';
import { THEME } from '../config/theme';
import { type BrandSurvey, makeCohortNamer, surveyLabel } from '../surveyShare';

export const ShareSurveysBanner: React.FunctionComponent = () => {
  const intl = useIntl();
  const { user } = useAuth();
  const jwtToken = user?.jwtToken ?? '';
  const organisationId = user?.organisation?.id ?? 0;
  const { activeBrand, cohorts } = useMarketerScope();
  const [expanded, setExpanded] = useState(false);

  const { data: surveysData } = useGetSurveysQuery(
    { jwtToken, organisationId },
    { skip: !jwtToken || !organisationId },
  );
  const cohortName = useMemo(() => makeCohortNamer(cohorts), [cohorts]);
  const cohortDesc = useMemo(() => {
    const byId = new Map(cohorts.map((c) => [c.id, c.description]));
    return (id?: number): string | undefined =>
      id != null ? byId.get(id) : undefined;
  }, [cohorts]);

  // Active surveys for this brand that have collected nothing yet.
  const unlaunched = useMemo<BrandSurvey[]>(() => {
    if (!activeBrand) return [];
    return ((surveysData as BrandSurvey[] | undefined) ?? []).filter(
      (s) =>
        (s.brandId ?? s.brand?.id) === activeBrand.id &&
        s.status === 'active' &&
        !s.accessCount,
    );
  }, [surveysData, activeBrand]);

  if (unlaunched.length === 0) return null;

  const copyLink = async (hash: string) => {
    const ok = await Utils.copyText(Utils.getPublicSurveyUrl(hash));
    if (ok) {
      message.success(
        intl.formatMessage({
          id: 'marketerView.insufficient.linkCopied',
          defaultMessage: 'Survey link copied',
        }),
      );
    }
  };

  return (
    <div
      style={{
        background: '#fff',
        border: `1px solid ${THEME.primary}`,
        borderRadius: 14,
        boxShadow: '0 4px 16px rgba(245, 158, 11, 0.12)',
        padding: expanded ? '18px 22px 22px' : '14px 22px',
        marginBottom: 18,
      }}
    >
      {/* Header — click to expand/collapse. */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => setExpanded((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') setExpanded((v) => !v);
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          cursor: 'pointer',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 0 }}>
          <ShareAltOutlined style={{ fontSize: 18, color: THEME.primaryDark, flexShrink: 0 }} />
          <span style={{ fontSize: 15.5, fontWeight: 700, color: THEME.primaryDark }}>
            <FormattedMessage
              id="marketerView.shareBanner.title"
              defaultMessage="Ready to launch, share {n, plural, one {your survey} other {your surveys}}"
              values={{ n: unlaunched.length }}
            />
          </span>
          <span
            style={{
              flexShrink: 0,
              fontSize: 12,
              fontWeight: 700,
              color: THEME.primaryDark,
              background: THEME.primarySoft,
              border: `1px solid ${THEME.primary}`,
              borderRadius: 999,
              padding: '1px 9px',
            }}
          >
            <FormattedMessage
              id="marketerView.shareBanner.count"
              defaultMessage="{n, plural, one {# survey} other {# surveys}}"
              values={{ n: unlaunched.length }}
            />
          </span>
        </div>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            flexShrink: 0,
            fontSize: 13,
            fontWeight: 600,
            color: THEME.primaryDark,
          }}
        >
          {expanded ? (
            <FormattedMessage id="marketerView.shareBanner.collapse" defaultMessage="Collapse" />
          ) : (
            <FormattedMessage id="marketerView.shareBanner.expand" defaultMessage="Click to expand" />
          )}
          {expanded ? <DownOutlined /> : <RightOutlined />}
        </span>
      </div>

      {/* Expanded — dashboard's full share content (survey cards + Email
          Platform Tips), styled in the marketer-view palette. */}
      {expanded && (
        <div style={{ marginTop: 20 }}>
          <p style={{ fontSize: 14, color: THEME.textSecondary, lineHeight: 1.5, margin: '0 0 20px' }}>
            <FormattedMessage
              id="marketerView.shareBanner.body"
              defaultMessage="Your {n, plural, one {survey is} other {surveys are}} ready to collect insights. Share {n, plural, one {this link} other {these links}} to start measuring your brand equity."
              values={{ n: unlaunched.length }}
            />
          </p>

          <Row gutter={[24, 24]} align="top">
            <Col xs={24} md={16}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {unlaunched.map((s) => {
                  const desc = cohortDesc(s.cohortId ?? s.cohort?.id);
                  return (
                    <div
                      key={s.hash}
                      style={{
                        padding: 16,
                        background: THEME.primarySoft,
                        borderRadius: 12,
                        border: `1px solid ${THEME.border}`,
                      }}
                    >
                      <div style={{ fontSize: 14, fontWeight: 700, color: THEME.textBase, marginBottom: desc ? 6 : 12 }}>
                        {surveyLabel(s, cohortName)}
                      </div>
                      {desc && (
                        <div style={{ fontSize: 12.5, color: THEME.textSecondary, lineHeight: 1.5, marginBottom: 12 }}>
                          {desc}
                        </div>
                      )}
                      {(s.countryCode || s.languageCode) && (
                        <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                          {s.countryCode && (
                            <span style={{ lineHeight: 1 }}>{renderFlagIcon(s.countryCode, 24)}</span>
                          )}
                          {s.languageCode && (
                            <span
                              style={{
                                fontSize: 12,
                                color: THEME.primaryDark,
                                background: '#fff',
                                padding: '2px 8px',
                                borderRadius: 6,
                                border: `1px solid ${THEME.border}`,
                              }}
                            >
                              {getLanguageName(s.languageCode)}
                            </span>
                          )}
                        </div>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <LinkOutlined style={{ color: THEME.primaryDark, fontSize: 16, flexShrink: 0 }} />
                        <Input
                          value={Utils.getPublicSurveyUrl(s.hash)}
                          readOnly
                          style={{ flex: 1, minWidth: 0, fontFamily: 'monospace', fontSize: 13, color: THEME.textBase }}
                        />
                        <Button
                          icon={<CopyOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            copyLink(s.hash);
                          }}
                          title={intl.formatMessage({
                            id: 'marketerView.insufficient.copy',
                            defaultMessage: 'Copy link',
                          })}
                          style={{ background: THEME.primary, border: 'none', color: '#fff', flexShrink: 0 }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Col>

            <Col xs={24} md={8}>
              <Card
                style={{ background: THEME.bgCard, border: `1px solid ${THEME.border}`, borderRadius: 16 }}
                styles={{ body: { padding: 20 } }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <SendOutlined style={{ color: THEME.primaryDark }} />
                  <span style={{ fontWeight: 700, color: THEME.textBase, fontSize: 15 }}>
                    <FormattedMessage
                      id="marketerView.shareBanner.emailTips"
                      defaultMessage="Email Platform Tips"
                    />
                  </span>
                </div>
                <div style={{ color: THEME.textSecondary, fontSize: 13, marginBottom: 16, fontWeight: 500 }}>
                  <FormattedMessage
                    id="marketerView.shareBanner.emailTipsBody"
                    defaultMessage="Replace {token} with the email address"
                    values={{ token: '{customer_identifier}' }}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {[
                    { name: 'Klaviyo', code: 'Replace with {{email}} or {{contact.email}}' },
                    { name: 'Mailchimp', code: 'Replace with *|EMAIL|*' },
                    { name: 'HubSpot', code: 'Replace with {{contact.email}}' },
                  ].map((p) => (
                    <div
                      key={p.name}
                      style={{
                        background: THEME.primarySoft,
                        padding: 12,
                        borderRadius: 8,
                        border: `1px solid ${THEME.border}`,
                      }}
                    >
                      <div style={{ fontWeight: 700, color: THEME.textBase, fontSize: 13 }}>
                        {p.name}:
                      </div>
                      <div style={{ color: THEME.textSecondary, fontSize: 12, fontFamily: 'monospace' }}>
                        {p.code}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
};
